import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, tap } from 'rxjs/operators';

import { AnalyticsService } from './services/analytics.service';

@Component({
    selector: 'app-root',
    template: '<router-outlet />',
    imports: [RouterOutlet]
})
export class AppComponent implements OnInit {
  title = 'Suite Amor Eterno';
  private static readonly PUBLIC_TRACKING_EXCLUDE = new Set(['/login', '/register', '/404', '/500']);

  readonly #destroyRef: DestroyRef = inject(DestroyRef);
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #titleService = inject(Title);
  readonly #analytics = inject(AnalyticsService);

  constructor() {
    this.#titleService.setTitle(this.title);
  }

  ngOnInit(): void {

    this.#router.events
      .pipe(
        filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd),
        tap(() => {
          this.updateDocumentTitle();
        }),
        tap(evt => {
          if (this.shouldTrackPageView(evt.urlAfterRedirects)) {
            this.#analytics.trackPageView(evt.urlAfterRedirects, this.#titleService.getTitle());
          }
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  private updateDocumentTitle(): void {
    const routeTitle = this.getDeepestRouteTitle(this.#activatedRoute);
    const fullTitle = routeTitle ? `${routeTitle} - ${this.title}` : this.title;
    this.#titleService.setTitle(fullTitle);
  }

  private getDeepestRouteTitle(route: ActivatedRoute): string | null {
    let current = route;
    while (current.firstChild) {
      current = current.firstChild;
    }

    return (current.snapshot.data?.['title'] as string | undefined) ?? null;
  }

  private shouldTrackPageView(url: string): boolean {
    const withoutQuery = url.split('?')[0] ?? '';
    const hashPath = withoutQuery.includes('#') ? (withoutQuery.split('#').pop() ?? '') : withoutQuery;
    const normalizedPath = hashPath.startsWith('/') ? hashPath : `/${hashPath}`;

    return !AppComponent.PUBLIC_TRACKING_EXCLUDE.has(normalizedPath);
  }
}
